import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getDefaultEnabledStepIdsForNewRequest } from '../config/requestFormStepRegistry';
import {
  checkScopeLoop,
  registrationFingerprint,
  scopeLoopLog,
  summarizeStepIds,
} from '../dev/scopeLoopDebug';
import {
  getAddableOptionalStepIds,
  getRemovableOptionalStepIds,
  mergeEnabledStepIdsAfterAdd,
  mergeEnabledStepIdsAfterRemove,
  resolveEnabledStepIds,
} from '../utils/resolveRequestFormSteps';

import type { RequestFormStepId } from '../config/requestFormStepRegistry';

export interface RequestScopeRegistration {
  storedStepIds: string[] | null | undefined;
  /** Show Add Scope / Remove scope chrome (Request Detail: when user may edit). */
  showScopeControls: boolean;
  /** Allow add/remove interactions (Request Detail: after Edit is active). */
  canManageScope: boolean;
  onScopeChange: (ids: RequestFormStepId[]) => void;
}

/** Serializable scope slice for lazy-loaded screens (#1330 web chunk boundary). */
export type RequestScopeSnapshot = {
  storedStepIds: string[] | null | undefined;
  enabledStepIds: readonly RequestFormStepId[];
  removableStepIds: readonly RequestFormStepId[];
};

interface RequestScopeContextValue {
  /** Raw persisted value; `null` = legacy full optional set. */
  storedStepIds: string[] | null | undefined;
  enabledStepIds: RequestFormStepId[];
  addableStepIds: RequestFormStepId[];
  removableStepIds: RequestFormStepId[];
  showScopeControls: boolean;
  canManageScope: boolean;
  addScope: (stepId: RequestFormStepId) => void;
  removeScope: (stepId: RequestFormStepId) => void;
  /** Revert optimistic scope without firing onScopeChange (PATCH failure / Cancel). */
  revertScope: (storedStepIds: string[] | null | undefined) => void;
  register: (registration: RequestScopeRegistration | null) => void;
}

const RequestScopeContext = createContext<RequestScopeContextValue | null>(
  null,
);

/** Provisional Create Request registration — avoids legacy-full flash before bridge sync. */
export function createNewRequestScopeRegistration(
  onScopeChange: RequestScopeRegistration['onScopeChange'],
): RequestScopeRegistration {
  return {
    storedStepIds: getDefaultEnabledStepIdsForNewRequest(),
    showScopeControls: true,
    canManageScope: true,
    onScopeChange,
  };
}

function storedStepIdsSemanticallyEqual(
  a: string[] | null | undefined,
  b: string[] | null | undefined,
): boolean {
  if (Object.is(a, b)) {
    return true;
  }
  const resolvedA = resolveEnabledStepIds(a);
  const resolvedB = resolveEnabledStepIds(b);
  if (resolvedA.length !== resolvedB.length) {
    return false;
  }
  return resolvedA.every((id, index) => id === resolvedB[index]);
}

export const RequestScopeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const registrationRef = useRef<RequestScopeRegistration | null>(null);
  const storedStepIdsRef = useRef<string[] | null | undefined>(null);
  const [storedStepIds, setStoredStepIds] = useState<
    string[] | null | undefined
  >(null);
  const [showScopeControls, setShowScopeControls] = useState(false);
  const [canManageScope, setCanManageScope] = useState(false);

  const enabledStepIds = useMemo(
    () => resolveEnabledStepIds(storedStepIds),
    [storedStepIds],
  );

  const addableStepIds = useMemo(
    () => getAddableOptionalStepIds(storedStepIds),
    [storedStepIds],
  );

  const removableStepIds = useMemo(
    () => getRemovableOptionalStepIds(storedStepIds),
    [storedStepIds],
  );

  useLayoutEffect(() => {
    storedStepIdsRef.current = storedStepIds;
  }, [storedStepIds]);

  const addScope = useCallback((stepId: RequestFormStepId) => {
    if (!registrationRef.current?.canManageScope) {
      return;
    }
    const next = mergeEnabledStepIdsAfterAdd(storedStepIdsRef.current, stepId);
    storedStepIdsRef.current = next;
    setStoredStepIds(next);
    registrationRef.current?.onScopeChange(next);
  }, []);

  const removeScope = useCallback((stepId: RequestFormStepId) => {
    if (!registrationRef.current?.canManageScope) {
      return;
    }
    const next = mergeEnabledStepIdsAfterRemove(
      storedStepIdsRef.current,
      stepId,
    );
    storedStepIdsRef.current = next;
    setStoredStepIds(next);
    registrationRef.current?.onScopeChange(next);
  }, []);

  const revertScope = useCallback((ids: string[] | null | undefined) => {
    storedStepIdsRef.current = ids;
    setStoredStepIds(ids);
  }, []);

  const register = useCallback(
    (registration: RequestScopeRegistration | null) => {
      const hadActiveRegistration = registrationRef.current != null;
      const fingerprint = registrationFingerprint(registration);
      const { shouldSkip } = checkScopeLoop('register', fingerprint);

      scopeLoopLog('register()', {
        hadActiveRegistration,
        fingerprint,
        incoming: registration
          ? {
              storedStepIds: summarizeStepIds(registration.storedStepIds),
              showScopeControls: registration.showScopeControls,
              canManageScope: registration.canManageScope,
            }
          : null,
        loopGuardSkipSetState: shouldSkip,
      });

      registrationRef.current = registration;

      if (shouldSkip) {
        scopeLoopLog('register() SKIP setState (loop guard)', { fingerprint });
        return;
      }

      if (registration) {
        setStoredStepIds(current => {
          const incoming = registration.storedStepIds;
          if (incoming == null && current == null) {
            scopeLoopLog('register() setStoredStepIds', {
              path: 'both-null',
              willChange: false,
            });
            return null;
          }

          if (!registration.canManageScope) {
            // Preserve explicit null (legacy DB) — do not widen to resolved legacy
            // array here; attachment scope helpers fall through to request.enabledStepIds.
            const next = incoming ?? null;
            scopeLoopLog('register() setStoredStepIds', {
              path: 'canManageScope-false',
              willChange: !Object.is(current, next),
              current: summarizeStepIds(current),
              next: summarizeStepIds(next),
            });
            return next;
          }

          const resolvedCurrent = resolveEnabledStepIds(current);
          const resolvedIncoming = resolveEnabledStepIds(incoming);
          const currentSet = new Set(resolvedCurrent);
          const incomingSet = new Set(resolvedIncoming);
          const incomingIsSubset =
            resolvedIncoming.length <= resolvedCurrent.length &&
            resolvedIncoming.every(id => currentSet.has(id));
          const currentIsWider =
            incomingIsSubset &&
            resolvedCurrent.length > resolvedIncoming.length;
          const currentIsSubset =
            resolvedCurrent.length <= resolvedIncoming.length &&
            resolvedCurrent.every(id => incomingSet.has(id));
          const incomingIsStrictSuperset =
            currentIsSubset && resolvedCurrent.length < resolvedIncoming.length;

          // addScope can widen context before CreateRequest re-registers stale formData (#1330).
          // removeScope can narrow context before registration sync — keep both during live re-sync.
          // Only apply when a screen is already registered — not when a new screen registers after unregister.
          if (hadActiveRegistration && currentIsWider) {
            const next = current ?? resolvedCurrent;
            scopeLoopLog('register() setStoredStepIds', {
              path: 'preserve-currentIsWider',
              willChange: !Object.is(current, next),
              current: summarizeStepIds(current),
              next: summarizeStepIds(next),
            });
            return next;
          }
          if (hadActiveRegistration && incomingIsStrictSuperset) {
            const next = current ?? resolvedCurrent;
            scopeLoopLog('register() setStoredStepIds', {
              path: 'preserve-incomingIsStrictSuperset',
              willChange: !Object.is(current, next),
              current: summarizeStepIds(current),
              next: summarizeStepIds(next),
            });
            return next;
          }

          const next = incoming ?? resolvedIncoming;
          if (storedStepIdsSemanticallyEqual(current, next)) {
            scopeLoopLog('register() setStoredStepIds', {
              path: 'apply-incoming-skip-semantic-equal',
              willChange: false,
              current: summarizeStepIds(current),
              next: summarizeStepIds(next),
            });
            return current;
          }
          scopeLoopLog('register() setStoredStepIds', {
            path: 'apply-incoming',
            willChange: !Object.is(current, next),
            current: summarizeStepIds(current),
            next: summarizeStepIds(next),
          });
          return next;
        });
        setShowScopeControls(prev => {
          const willChange = prev !== registration.showScopeControls;
          scopeLoopLog('register() setShowScopeControls', {
            prev,
            next: registration.showScopeControls,
            willChange,
          });
          return willChange ? registration.showScopeControls : prev;
        });
        setCanManageScope(prev => {
          const willChange = prev !== registration.canManageScope;
          scopeLoopLog('register() setCanManageScope', {
            prev,
            next: registration.canManageScope,
            willChange,
          });
          return willChange ? registration.canManageScope : prev;
        });
      } else {
        scopeLoopLog('register() unregister setState', {
          setStoredStepIds: true,
          setShowScopeControls: true,
          setCanManageScope: true,
        });
        setStoredStepIds(null);
        setShowScopeControls(false);
        setCanManageScope(false);
      }
    },
    [],
  );

  const value = useMemo(
    () => ({
      storedStepIds,
      enabledStepIds,
      addableStepIds,
      removableStepIds,
      showScopeControls,
      canManageScope,
      addScope,
      removeScope,
      revertScope,
      register,
    }),
    [
      storedStepIds,
      enabledStepIds,
      addableStepIds,
      removableStepIds,
      showScopeControls,
      canManageScope,
      addScope,
      removeScope,
      revertScope,
      register,
    ],
  );

  return (
    <RequestScopeContext.Provider value={value}>
      {children}
    </RequestScopeContext.Provider>
  );
};

export function useRequestScope(): Omit<
  RequestScopeContextValue,
  'register'
> | null {
  const value = useContext(RequestScopeContext);
  return useMemo(() => {
    if (!value) {
      return null;
    }
    const { register: _register, ...rest } = value;
    return rest;
  }, [
    value?.storedStepIds,
    value?.enabledStepIds,
    value?.addableStepIds,
    value?.removableStepIds,
    value?.showScopeControls,
    value?.canManageScope,
    value?.addScope,
    value?.removeScope,
    value?.revertScope,
  ]);
}

/** Active Create Request / Request Detail screen registers scope handlers. */
export function useRegisterRequestScope(
  registration: RequestScopeRegistration | null,
): void {
  const scope = useContext(RequestScopeContext);
  const onScopeChangeRef = useRef(registration?.onScopeChange);
  onScopeChangeRef.current = registration?.onScopeChange;

  useLayoutEffect(() => {
    if (!scope) {
      return undefined;
    }
    if (!registration) {
      scopeLoopLog('useRegisterRequestScope layout effect', {
        action: 'register(null)',
      });
      scope.register(null);
      return undefined;
    }

    const fingerprint = registrationFingerprint(registration);
    const { shouldSkip } = checkScopeLoop(
      'useRegisterRequestScope',
      fingerprint,
    );
    scopeLoopLog('useRegisterRequestScope layout effect', {
      action: shouldSkip ? 'SKIP register (loop guard)' : 'register(payload)',
      fingerprint,
      deps: {
        storedStepIds: summarizeStepIds(registration.storedStepIds),
        showScopeControls: registration.showScopeControls,
        canManageScope: registration.canManageScope,
      },
    });
    if (shouldSkip) {
      return undefined;
    }

    scope.register({
      storedStepIds: registration.storedStepIds,
      showScopeControls: registration.showScopeControls,
      canManageScope: registration.canManageScope,
      onScopeChange: ids => onScopeChangeRef.current?.(ids),
    });
  }, [
    scope?.register,
    registration?.storedStepIds,
    registration?.showScopeControls,
    registration?.canManageScope,
  ]);

  useLayoutEffect(() => {
    const unregister = scope?.register;
    if (!unregister) {
      return undefined;
    }
    return () => unregister(null);
  }, [scope?.register]);
}
